// TUT-308 — a entrada escolhida no login (consultoria/advogado/assistente) e
// o aviso de descompasso quando a conta não bate com a entrada escolhida.
//
// A entrada NUNCA decide acesso — quem decide é o servidor, pela CONTA
// (roleKey EXTERNAL + profissão do vínculo, ver App.tsx `TenantGate`). Isto
// aqui é só copy: qual título/ajuda mostrar no login (LoginPage), e qual
// aviso mostrar — uma única vez — se a pessoa escolheu um caminho e a conta
// dela abre outro.

import { lerLocal, gravarLocal, apagarLocal } from "@/lib/armazenamento-local";

export type Entrada = "consultoria" | "advogado" | "assistente";

const CHAVE_SESSION_STORAGE = "tutelae:entrada-login";

function valida(valor: string | null): Entrada | null {
  return valor === "consultoria" || valor === "advogado" || valor === "assistente"
    ? valor
    : null;
}

/**
 * Grava a entrada escolhida no MOMENTO em que o login deu certo — não a cada
 * clique na aba — para sobreviver à navegação até o portão do escritório
 * decidir o destino real (ver `TenantGate`/`PortalPage`, que leem e limpam).
 * `sessionStorage`, embrulhado em try/catch como o resto da casa
 * (`geocoding.ts`, `AgendaPage.tsx`): sem ele o aviso simplesmente não
 * aparece, o login segue normal.
 */
export function salvarEntradaEscolhida(entrada: Entrada) {
  try {
    gravarLocal(CHAVE_SESSION_STORAGE, entrada, "sessao");
  } catch {
    // modo privado, quota excedida — sem aviso, sem quebra.
  }
}

/** Lê sem consumir — quem decide quando limpar é quem sabe o destino real. */
export function lerEntradaEscolhida(): Entrada | null {
  try {
    return valida(lerLocal(CHAVE_SESSION_STORAGE, "sessao"));
  } catch {
    return null;
  }
}

export function limparEntradaEscolhida() {
  try {
    apagarLocal(CHAVE_SESSION_STORAGE, "sessao");
  } catch {
    // nada para limpar se nunca gravou.
  }
}

export interface DestinoLogin {
  /** Sessão EXTERNAL, ou seja, foi para o portal — nunca decide, só descreve. */
  external: boolean;
  /** Só importa quando `external` é true; ignorado (pode ser `null`) senão. */
  profession: "COUNSEL" | "EXPERT" | null;
}

function entradaDoDestino(destino: DestinoLogin): Entrada {
  if (!destino.external) return "consultoria";
  return destino.profession === "EXPERT" ? "assistente" : "advogado";
}

/**
 * Função pura: entrada escolhida × destino real → texto do aviso, ou `null`
 * quando bateram (ou quando não havia entrada registrada). Tabela de casos em
 * `entrada-login.test.ts`.
 */
export function avisoDescompasso(
  entrada: Entrada | null,
  destino: DestinoLogin,
): string | null {
  if (!entrada) return null;
  const entradaReal = entradaDoDestino(destino);
  if (entradaReal === entrada) return null;
  if (entradaReal === "consultoria")
    return "Sua conta é da equipe — abrimos o sistema.";
  if (entradaReal === "advogado")
    return "Sua conta é de advogado — abrimos o portal do advogado.";
  return "Sua conta é de assistente — abrimos o portal do assistente.";
}
